import React, { useEffect, useState } from 'react';

import { cronCadence } from './format';
import { fetchScopes } from './queries/configuration';
import './ConfigurationList.scss';

// Every GMPRO schedule is stated in SAST (see Gmpro::RunWindow, the config
// knob catalogue's own descriptions), regardless of the operator's browser.
const SAST_TIME_ZONE = 'Africa/Johannesburg';

// The three states an operator sees, named as the product owner named them:
// Enabled, Partial, Disabled. The `excluded` KEY stays as it is — it is the
// serializer's word (Gmpro::ScopeSummary), and renaming it would break the
// payload contract for a display change.
//
// Label and colour live in one entry per status rather than two parallel maps:
// keyed separately, a new status can be added to one and forgotten in the other,
// and the miss shows up as a pill with the right colour and the wrong word
// rather than as an error.
const STATUS = {
  enabled: { label: 'Enabled', modifier: 'gmpro-config-list__pill--on' },
  excluded: { label: 'Disabled', modifier: 'gmpro-config-list__pill--off' },
  partial: { label: 'Partial', modifier: 'gmpro-config-list__pill--mix' },
  none: { label: 'No regions', modifier: 'gmpro-config-list__pill--quiet' },
};

/**
 * Renders a scope's status as the design system's pill: green for enabled,
 * red for excluded, orange for partial (some but not all regions on — only
 * possible for the Global rollup row), and a quiet neutral fallback for the
 * "no regions configured yet" edge case.
 *
 * @param {Object} props
 * @param {string} props.status - "enabled", "excluded", "partial", or "none".
 * @returns {JSX.Element}
 */
function StatusPill({ status }) {
  const { label, modifier } = STATUS[status] || { label: 'Unknown', modifier: STATUS.none.modifier };

  return (
    <span className={`gmpro-config-list__pill ${modifier}`}>
      <span className="gmpro-config-list__dot" />
      {label}
    </span>
  );
}

/**
 * Formats an ISO 8601 timestamp as a short "next run" moment in SAST, e.g.
 * "Mon 04:00". Renders the server-computed occurrence
 * (Gmpro::Schedule.next_run_at) rather than re-deriving one client-side from
 * the cron expression: the store accepts any cron Fugit can parse, not only
 * the single weekly shape formatCron summarizes, so the timestamp is the one
 * value guaranteed to agree with what will actually run.
 *
 * @param {string} isoTimestamp
 * @returns {string}
 */
function formatNextRunAt(isoTimestamp) {
  const date = new Date(isoTimestamp);
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone: SAST_TIME_ZONE, weekday: 'short' }).format(date);
  const time = new Intl.DateTimeFormat('en-GB', {
    timeZone: SAST_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date);

  return `${weekday} ${time}`;
}

/**
 * Renders the "Next run" cell for one scope row. An excluded scope never
 * runs, whatever its cron says. A scope with no cron at all is manual-only.
 * A scope with a cron reads its server-computed next_run_at, never a
 * client-side summary of the cron expression — formatCron only understands
 * the single weekly shape, so a plain daily cron (the knob catalogue's own
 * documented example, "0 4 * * *") would otherwise render as "Not scheduled
 * — manual only" on the one surface an operator checks before deciding
 * whether a region goes out tonight. If a cron is somehow present without a
 * computed next_run_at, the raw cron is shown rather than either a
 * fabricated summary or a false "manual only".
 *
 * @param {Object} props
 * @param {Object} props.scope - One row from fetchScopes().
 * @returns {JSX.Element}
 */
function NextRunCell({ scope }) {
  // Global never runs: it is the defaults layer regions inherit from. Saying
  // "manual only" here would imply it could be triggered, which it cannot.
  if (scope.scope === 'global') {
    return <span className="gmpro-config-list__quiet">Never runs — defaults for regions</span>;
  }

  if (scope.status === 'excluded') {
    return <span className="gmpro-config-list__quiet">Disabled</span>;
  }

  if (!scope.cron) {
    return <span className="gmpro-config-list__quiet">Not scheduled — manual only</span>;
  }

  const primary = scope.next_run_at ? formatNextRunAt(scope.next_run_at) : scope.cron;
  // A next occurrence alone cannot tell a daily run from a weekly one — both
  // render as "Mon 04:00". Omitted when the shape cannot be named rather than
  // guessed at.
  const cadence = cronCadence(scope.cron);

  return (
    <>
      <span>{primary}</span>
      {cadence && <span className="gmpro-config-list__cadence"> · {cadence}</span>}
    </>
  );
}

/**
 * Renders the "Differs from default" cell. Global's override_count arrives
 * as null, deliberately — there is no such concept at the defaults layer —
 * and renders as a muted em-dash rather than a misleading zero. A real
 * override count of zero (a region that has never diverged) is also muted,
 * because orange on this page means one thing only: this value differs from
 * the default, and zero does not. Any other count renders in orange.
 *
 * @param {Object} props
 * @param {?number} props.overrideCount
 * @returns {JSX.Element}
 */
function OverridesCell({ overrideCount }) {
  if (overrideCount === null || overrideCount === undefined) {
    return <span className="gmpro-config-list__quiet">—</span>;
  }

  if (overrideCount === 0) {
    return <span className="gmpro-config-list__zero">0 settings</span>;
  }

  const noun = overrideCount === 1 ? 'setting' : 'settings';

  return <span className="gmpro-config-list__diverge">{overrideCount} {noun}</span>;
}

/**
 * Screen 1 of the rebuilt GMPRO configuration page: the scope list. Global
 * defaults are pinned first and tinted, labelled as the layer every region
 * inherits from rather than as a region of its own — seeing it above the
 * regions is what teaches the inheritance model, without a paragraph
 * explaining it. The Enabled switch deliberately does not appear here; a
 * one-click destructive toggle in a table row is too easy to hit by
 * accident, and it lives on each scope's own detail page instead.
 *
 * @returns {JSX.Element}
 */
export default function ConfigurationList() {
  const [scopes, setScopes] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let mounted = true;

    fetchScopes()
      .then((payload) => {
        if (mounted) setScopes(payload.scopes);
      })
      .catch(() => {
        // An empty table reads as "no regions configured" — indistinguishable
        // from a real (if unlikely) state — so a failed fetch says so rather
        // than rendering a confident-looking empty list.
        if (mounted) setLoadFailed(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="gmpro-config-list">
      <h1 className="gmpro-config-list__title">GMPRO Configuration</h1>
      <p className="gmpro-config-list__subtitle">Solver settings and schedules, per region.</p>

      {loadFailed && (
        <p className="gmpro-config-list__load-error" data-testid="gmpro-config-list-error">
          Couldn&rsquo;t load the configuration list. Reload the page to try again.
        </p>
      )}

      {!loadFailed && (
        <table className="gmpro-config-list__table" data-testid="gmpro-config-list-table">
          <thead>
            <tr>
              <th>Scope</th>
              <th>Status</th>
              <th>Next run</th>
              <th className="gmpro-config-list__r">Companies</th>
              <th className="gmpro-config-list__r">Differs from default</th>
            </tr>
          </thead>
          <tbody>
            {scopes.map((scope) => {
              const isGlobal = scope.scope === 'global';

              return (
                <tr
                  key={scope.scope}
                  className={isGlobal ? 'gmpro-config-list__row--defaults' : undefined}
                  data-testid={`gmpro-config-list-row-${scope.scope}`}
                >
                  <td>
                    <a
                      className="gmpro-config-list__name"
                      href={`/admin/gmpro_runs/configuration/${scope.scope}`}
                    >
                      {scope.name}
                    </a>
                    {isGlobal && (
                      <span className="gmpro-config-list__role">Every region inherits these</span>
                    )}
                  </td>
                  <td>
                    <StatusPill status={scope.status} />
                  </td>
                  <td>
                    <NextRunCell scope={scope} />
                  </td>
                  <td className="gmpro-config-list__r gmpro-config-list__num">
                    {scope.companies_total === null || scope.companies_total === undefined
                      ? <span className="gmpro-config-list__quiet">—</span>
                      : <>{scope.companies_running} of {scope.companies_total}</>}
                  </td>
                  <td className="gmpro-config-list__r gmpro-config-list__num">
                    <OverridesCell overrideCount={scope.override_count} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
